import { useState, useEffect } from 'react';

import { IdleOptions } from './definitions/types';

/**
 * A custom hook that detects when the user is idle based on a specified time limit.
 * @param {IdleOptions} options - The options for the idle detection.
 * @param {boolean} options.isDisabled - Whether the idle detection is disabled.
 * @param {Function} options.onIdle - The function to call when the user becomes idle.
 * @param {number} options.timeToIdle - The time limit (in milliseconds) for the user to become idle.
 * @returns {boolean} Whether the user is currently idle.
 */
const useIdle = ({ isDisabled, onIdle, timeToIdle }: IdleOptions): boolean => {
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    let timeout: NodeJS.Timeout;

    const resetTimeout = (): void => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setIsIdle(true);
        if (onIdle) {
          onIdle();
        }
      }, timeToIdle);
    };

    const handleEvent = (): void => {
      setIsIdle(false);
      resetTimeout();
    };

    const handleVisibilityChange = (): void => {
      if (!document.hidden) {
        handleEvent();
      }
    };

    resetTimeout();

    // Read more about passive event listeners here:
    // https://web.dev/uses-passive-event-listeners/
    window.addEventListener('mousemove', handleEvent, { passive: true });
    window.addEventListener('mousedown', handleEvent, { passive: true });
    window.addEventListener('resize', handleEvent, { passive: true });
    window.addEventListener('keydown', handleEvent, { passive: true });
    window.addEventListener('touchstart', handleEvent, { passive: true });
    window.addEventListener('wheel', handleEvent, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return (): void => {
      window.removeEventListener('mousemove', handleEvent);
      window.removeEventListener('mousedown', handleEvent);
      window.removeEventListener('resize', handleEvent);
      window.removeEventListener('keydown', handleEvent);
      window.removeEventListener('touchstart', handleEvent);
      window.removeEventListener('wheel', handleEvent);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(timeout);
    };
  }, [timeToIdle, isDisabled]);

  return isIdle;
};

export default useIdle;
